import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const AIContentPanel = ({ onGenerateContent, generatedContent, isGenerating, pressKitData }) => {
  const [activeContentType, setActiveContentType] = useState('press-release');
  const [contentParams, setContentParams] = useState({});
  const [customPrompt, setCustomPrompt] = useState('');

  // Content generation options
  const contentTypes = [
    {
      id: 'press-release',
      name: 'Press Release',
      icon: 'FileText',
      description: 'Generate professional press releases with AP style guidelines',
      fields: [
        { id: 'tone', label: 'Tone', type: 'select', options: ['professional', 'friendly', 'authoritative', 'conversational'] },
        { id: 'length', label: 'Length', type: 'select', options: ['short', 'medium', 'long'] },
        { id: 'focus', label: 'Focus', type: 'select', options: ['book-launch', 'author-interview', 'award-announcement', 'series-continuation'] }
      ]
    },
    {
      id: 'author-bio',
      name: 'Author Biography',
      icon: 'User',
      description: 'Create compelling author biographies highlighting achievements',
      fields: [
        { id: 'authorName', label: 'Author Name', type: 'text', placeholder: 'Enter author name' },
        { id: 'style', label: 'Style', type: 'select', options: ['formal', 'casual', 'creative', 'academic'] },
        { id: 'length', label: 'Length', type: 'select', options: ['brief', 'standard', 'detailed'] }
      ]
    },
    {
      id: 'social-media',
      name: 'Social Media Content',
      icon: 'Share2',
      description: 'Generate engaging social media posts for multiple platforms',
      fields: [
        { id: 'platform', label: 'Platform', type: 'select', options: ['twitter', 'facebook', 'instagram', 'linkedin', 'tiktok'] },
        { id: 'contentStyle', label: 'Content Style', type: 'select', options: ['promotional', 'educational', 'behind-the-scenes', 'interactive'] },
        { id: 'callToAction', label: 'Call to Action', type: 'select', options: ['buy-now', 'learn-more', 'follow', 'share'] }
      ]
    },
    {
      id: 'story-angles',
      name: 'Story Angles',
      icon: 'Lightbulb',
      description: 'Discover unique story angles for media pitches',
      fields: [
        { id: 'mediaType', label: 'Target Media', type: 'select', options: ['mainstream', 'literary-magazines', 'podcasts', 'blogs', 'local-news'] },
        { id: 'angleType', label: 'Angle Type', type: 'select', options: ['human-interest', 'trend-focused', 'expert-commentary', 'breaking-news'] },
        { id: 'urgency', label: 'Urgency', type: 'select', options: ['timely', 'evergreen', 'seasonal', 'trending'] }
      ]
    },
    {
      id: 'blog-post',
      name: 'Blog Content',
      icon: 'Edit3',
      description: 'Create SEO-optimized blog posts and articles',
      fields: [
        { id: 'topic', label: 'Topic', type: 'text', placeholder: 'Enter blog topic' },
        { id: 'audience', label: 'Target Audience', type: 'select', options: ['readers', 'writers', 'industry-professionals', 'general-public'] },
        { id: 'seoFocus', label: 'SEO Focus', type: 'text', placeholder: 'Primary keyword' }
      ]
    }
  ];

  const currentContentType = contentTypes?.find(type => type?.id === activeContentType);

  const handleGenerateContent = async () => {
    await onGenerateContent?.(activeContentType, {
      ...contentParams,
      customPrompt,
      pressKitMetadata: pressKitData
    });
  };

  const handleFieldChange = (fieldId, value) => {
    setContentParams(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const renderField = (field) => {
    switch (field?.type) {
      case 'select':
        return (
          <select
            value={contentParams?.[field?.id] || ''}
            onChange={(e) => handleFieldChange(field?.id, e?.target?.value)}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Select {field?.label}</option>
            {field?.options?.map(option => (
              <option key={option} value={option}>
                {option?.charAt(0)?.toUpperCase() + option?.slice(1)?.replace(/-/g, ' ')}
              </option>
            ))}
          </select>
        );
      case 'text':
        return (
          <input
            type="text"
            placeholder={field?.placeholder}
            value={contentParams?.[field?.id] || ''}
            onChange={(e) => handleFieldChange(field?.id, e?.target?.value)}
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="w-96 bg-white border-r border-slate-200 flex flex-col">
      {/* AI Panel Header */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center space-x-3 mb-3">
          <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
            <Icon name="Sparkles" size={16} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">AI Content Studio</h3>
            <p className="text-xs text-slate-500">Generate professional content instantly</p>
          </div>
        </div>

        {/* Generation Status */}
        {isGenerating && (
          <div className="flex items-center space-x-2 p-3 bg-indigo-50 rounded-lg">
            <Icon name="Loader2" size={16} className="text-indigo-600 animate-spin" />
            <span className="text-sm text-indigo-700">Generating content...</span>
          </div>
        )}
      </div>
      {/* Content Type Selection */}
      <div className="p-4 border-b border-slate-200">
        <h4 className="text-sm font-medium text-slate-900 mb-3">Content Type</h4>
        <div className="grid grid-cols-1 gap-2">
          {contentTypes?.map((type) => (
            <button
              key={type?.id}
              onClick={() => setActiveContentType(type?.id)}
              className={`p-3 text-left rounded-lg border transition-all ${
                activeContentType === type?.id
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700' :'border-slate-200 hover:border-slate-300 text-slate-700'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Icon name={type?.icon} size={16} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{type?.name}</div>
                  <div className="text-xs text-slate-500 mt-1 line-clamp-2">
                    {type?.description}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
      {/* Content Parameters */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-4">
          <h4 className="text-sm font-medium text-slate-900">
            {currentContentType?.name} Settings
          </h4>
          
          {/* Dynamic Fields */}
          <div className="space-y-3">
            {currentContentType?.fields?.map((field) => (
              <div key={field?.id}>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  {field?.label}
                </label>
                {renderField(field)}
              </div>
            ))}
          </div>

          {/* Book Metadata Integration */}
          <div className="p-3 bg-slate-50 rounded-lg">
            <h5 className="text-xs font-medium text-slate-700 mb-2">Auto-filled from Press Kit</h5>
            <div className="space-y-1 text-xs text-slate-600">
              <div>Title: {pressKitData?.title}</div>
              <div>Genre: {pressKitData?.genre}</div>
              <div>Publisher: {pressKitData?.publisher}</div>
              <div>Key Themes: {pressKitData?.keyThemes?.join(', ')}</div>
            </div>
          </div>

          {/* Custom Prompt */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Custom Instructions (Optional)
            </label>
            <textarea
              placeholder="Add specific instructions or additional context..."
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e?.target?.value)}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            />
          </div>
        </div>
      </div>
      {/* Generation Actions */}
      <div className="p-4 border-t border-slate-200 space-y-3">
        <button
          onClick={handleGenerateContent}
          disabled={isGenerating}
          className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Icon name={isGenerating ? "Loader2" : "Sparkles"} size={16} className={isGenerating ? "animate-spin" : ""} />
          <span className="font-medium">
            {isGenerating ? 'Generating...' : `Generate ${currentContentType?.name}`}
          </span>
        </button>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-2">
          <button className="flex items-center justify-center space-x-1 px-3 py-2 text-sm border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors">
            <Icon name="RotateCcw" size={14} />
            <span>Reset</span>
          </button>
          <button className="flex items-center justify-center space-x-1 px-3 py-2 text-sm border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors">
            <Icon name="Save" size={14} />
            <span>Save Preset</span>
          </button>
        </div>
      </div>
      {/* Generated Content Preview */}
      {Object?.keys(generatedContent)?.length > 0 && (
        <div className="border-t border-slate-200 max-h-40 overflow-y-auto">
          <div className="p-4">
            <h4 className="text-sm font-medium text-slate-900 mb-3 flex items-center space-x-2">
              <Icon name="FileCheck" size={16} />
              <span>Generated Content</span>
            </h4>
            <div className="space-y-2">
              {Object?.entries(generatedContent)?.map(([type, content]) => (
                <div key={type} className="p-2 bg-green-50 border border-green-200 rounded-lg">
                  <div className="text-xs font-medium text-green-800 capitalize mb-1">
                    {type?.replace(/-/g, ' ')}
                  </div>
                  <div className="text-xs text-green-700 line-clamp-2">
                    {typeof content === 'string' ? content : content?.headline || content?.title || 'Generated successfully'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIContentPanel;